/**
 * Панель управления слоями: видимость, прозрачность, выбор подложки.
 *
 * Прозрачность вынесена в интерфейс не для красоты. Функциональное зонирование
 * читают вместе с подложкой: чтобы понять, что именно попадает в зону, нужно
 * видеть под ней улицы и здания. Фиксированная прозрачность не годится —
 * на плотной застройке нужен один баланс, на пустырях другой.
 */

import { type AppTheme, THEME_LABELS } from "./theme";
import type { ThematicLayerGroup } from "~/map/thematicLayers";
import { el } from "./dom";

export interface LayerPanelCallbacks {
  onToggleLayer: (key: string, visible: boolean) => void;
  onOpacityChange: (key: string, opacity: number) => void;
  onThemeChange: (theme: AppTheme) => void;
}

export interface LayerPanelOptions extends LayerPanelCallbacks {
  groups: ThematicLayerGroup[];
  visibleKeys: ReadonlySet<string>;
  opacities: ReadonlyMap<string, number>;
  theme: AppTheme;
}

export function createLayerPanel(options: LayerPanelOptions): HTMLElement {
  const { groups, visibleKeys, opacities, theme } = options;

  const themeControls = el(
    "fieldset",
    { class: "panel__fieldset" },
    el("legend", { class: "panel__legend", text: "Тема" }),
    el(
      "div",
      { class: "panel__radios" },
      ...(Object.keys(THEME_LABELS) as AppTheme[]).map((value) =>
        el(
          "label",
          { class: "panel__radio" },
          el("input", {
            attrs: {
              type: "radio",
              name: "basemap-theme",
              value,
              ...(value === theme ? { checked: true } : {}),
            },
            on: {
              change: (event) => {
                if ((event.target as HTMLInputElement).checked) {
                  options.onThemeChange(value);
                }
              },
            },
          }),
          el("span", { text: THEME_LABELS[value] }),
        ),
      ),
    ),
  );

  const layerControls =
    groups.length === 0
      ? el("p", {
          class: "panel__empty",
          text: "Слои генплана ещё не опубликованы. Соберите их командой tiles:layer — карта подхватит их без пересборки.",
        })
      : el(
          "fieldset",
          { class: "panel__fieldset" },
          el("legend", { class: "panel__legend", text: "Слои генплана" }),
          ...groups.map((group) => layerRow(group, visibleKeys.has(group.key), opacities.get(group.key) ?? 0.7, options)),
        );

  return el("div", { class: "panel__content" }, themeControls, layerControls);
}

function layerRow(
  group: ThematicLayerGroup,
  visible: boolean,
  opacity: number,
  callbacks: LayerPanelCallbacks,
): HTMLElement {
  const opacityId = `opacity-${group.key}`;

  const slider = el("input", {
    class: "layer-row__slider",
    attrs: {
      type: "range",
      min: "0",
      max: "100",
      step: "5",
      value: String(Math.round(opacity * 100)),
      id: opacityId,
      "aria-label": `Прозрачность слоя «${group.title}»`,
      ...(visible ? {} : { disabled: true }),
    },
    on: {
      input: (event) => {
        const value = Number((event.target as HTMLInputElement).value) / 100;
        callbacks.onOpacityChange(group.key, value);
      },
    },
  });

  const checkbox = el("input", {
    attrs: {
      type: "checkbox",
      ...(visible ? { checked: true } : {}),
    },
    on: {
      change: (event) => {
        const checked = (event.target as HTMLInputElement).checked;
        slider.disabled = !checked;
        callbacks.onToggleLayer(group.key, checked);
      },
    },
  });

  return el(
    "div",
    { class: "layer-row" },
    el(
      "label",
      { class: "layer-row__head" },
      checkbox,
      el("span", {
        class: "layer-row__swatch",
        attrs: { style: `background:${group.accentColor}`, "aria-hidden": "true" },
      }),
      el("span", { class: "layer-row__title", text: group.title }),
    ),
    slider,
    // Оговорка о происхождении листа. Молчать нельзя: человек вправе знать,
    // откуда чертёж и можно ли по нему судить о конкретном участке. Текст
    // берётся из описания слоя — у разных листов точность разная.
    group.note
      ? el("p", { class: "layer-row__note", text: group.note })
      : null,
  );
}
